import { useMemo, useRef, type MutableRefObject } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { Side, Simulation } from '@/domain/hs1'
import { SIDES } from '@/domain/hs1'

/**
 * Was die Rechnung ausserhalb des Körpers zu zeigen hat.
 *
 * DIE FIGUR ZEIGT DIE BEWEGUNG, NICHT DIE KINETIK. Wo der Schwerpunkt liegt,
 * wo die Bodenreaktion angreift, wie gross sie ist — das steht in den
 * Diagrammen und ist dort schwer mit der Bewegung zusammenzubringen. Hier steht
 * es an derselben Figur.
 *
 * ALLES KOMMT AUS DEM RECHENKERN und wird hier nur gezeichnet. Kein Wert
 * entsteht in dieser Datei; sie hat keine Formel. Wer eine Zahl im Bild sieht,
 * findet dieselbe Zahl in einer Tabelle.
 *
 * DIE PHASE WIRD GETEILT, nicht zweimal gezählt. Die Figur führt sie in einer
 * Referenz — zwei getrennte Uhren liefen auseinander, und der Schwerpunkt
 * schwebte neben einem Körper, zu dem er nicht gehört.
 */

export interface OverlayFlags {
  /** Gelenkpunkte als kleine Kugeln. */
  markers: boolean
  /** Bahnkurven der Füsse und Hände über den Zyklus. */
  paths: boolean
  /** Schwerpunkt und seine Bahn. */
  com: boolean
  /** Bodenreaktionskraft als Pfeil je Fuss. */
  grf: boolean
  /** Druckmittelpunkt je Fuss. */
  cop: boolean
}

export const DEFAULT_OVERLAYS: OverlayFlags = {
  markers: false,
  paths: false,
  com: false,
  grf: false,
  cop: false,
}

export const OVERLAY_LABELS: Record<keyof OverlayFlags, { label: string; note: string }> = {
  markers: { label: 'Marker', note: 'Gelenkpunkte als Kugeln' },
  paths: { label: 'Bahnkurven', note: 'Füsse und Hände über den ganzen Zyklus' },
  com: { label: 'Schwerpunkt', note: 'Lage und Bahn des Gesamtschwerpunkts' },
  grf: { label: 'Bodenkraft', note: 'Betrag und Richtung je Fuss' },
  cop: { label: 'Druckmittelpunkt', note: 'Angriffspunkt der Bodenreaktion' },
}

/**
 * Wie viele Körpergewichte ein Pfeil von einem Meter Länge bedeutet.
 *
 * Der Massstab steht als Konstante und nicht als automatische Anpassung: ein
 * Pfeil, der sich selbst skaliert, sieht bei jedem Gang gleich lang aus und
 * sagt damit nichts mehr über den Unterschied zwischen Gehen und Springen.
 */
const GRF_PER_METRE = 2

const MARKER_NODES = ['hip', 'knee', 'ankle', 'mtp', 'shoulder', 'elbow', 'wrist'] as const

/** Punkte, deren Bahn gezeichnet wird. */
const PATH_NODES = ['ankle', 'wrist'] as const

export function Overlays({
  simulation,
  flags,
  phase,
  accent,
  warm,
}: {
  simulation: Simulation
  flags: OverlayFlags
  /** Die Phase der Figur — geteilt, damit beide dasselbe zeigen. */
  phase: MutableRefObject<number>
  accent: THREE.ColorRepresentation
  warm: THREE.ColorRepresentation
}) {
  const { dynamics } = simulation
  const n = dynamics.samples

  /* Die Bahnen ändern sich nur mit der Rechnung, nicht je Bild. */
  const paths = useMemo(() => {
    const out: { key: string; points: Float32Array }[] = []
    for (const node of PATH_NODES) {
      for (const side of SIDES) {
        const track = dynamics.tracks[`${node}${side}`]
        if (!track) continue
        const points = new Float32Array((n + 1) * 3)
        for (let i = 0; i <= n; i++) {
          const p = track[i % n]
          points[i * 3] = p.x
          points[i * 3 + 1] = p.y
          points[i * 3 + 2] = p.z
        }
        out.push({ key: `${node}${side}`, points })
      }
    }
    return out
  }, [dynamics, n])

  const comPath = useMemo(() => {
    const points = new Float32Array((n + 1) * 3)
    for (let i = 0; i <= n; i++) {
      const p = dynamics.com[i % n]
      points[i * 3] = p.x
      points[i * 3 + 1] = p.y
      points[i * 3 + 2] = p.z
    }
    return points
  }, [dynamics, n])

  const markers = useRef<THREE.InstancedMesh>(null)
  const comBall = useRef<THREE.Mesh>(null)
  const copR = useRef<THREE.Mesh>(null)
  const copL = useRef<THREE.Mesh>(null)
  const grfR = useRef<THREE.Group>(null)
  const grfL = useRef<THREE.Group>(null)

  const dummy = useMemo(() => new THREE.Object3D(), [])
  const markerCount = MARKER_NODES.length * SIDES.length

  useFrame(() => {
    const i = Math.min(n - 1, Math.max(0, Math.floor(phase.current * n)))

    if (flags.markers && markers.current) {
      let k = 0
      for (const node of MARKER_NODES) {
        for (const side of SIDES) {
          const track = dynamics.tracks[`${node}${side}`]
          const p = track ? track[i] : null
          dummy.position.set(p?.x ?? 0, p?.y ?? -10, p?.z ?? 0)
          dummy.updateMatrix()
          markers.current.setMatrixAt(k++, dummy.matrix)
        }
      }
      markers.current.instanceMatrix.needsUpdate = true
    }

    if (flags.com && comBall.current) {
      const c = dynamics.com[i]
      comBall.current.position.set(c.x, c.y, c.z)
    }

    if (flags.cop) {
      for (const [side, ref] of [
        ['R', copR],
        ['L', copL],
      ] as const) {
        const mesh = ref.current
        if (!mesh) continue
        const p = dynamics.cop[side][i]
        const load = dynamics.load[side][i]
        mesh.position.set(p.x, 0.004, p.z)
        /* Ein unbelasteter Fuss hat keinen Druckmittelpunkt. Ihn trotzdem zu
           zeigen hiesse, einen Angriffspunkt für eine Kraft zu behaupten, die
           null ist — der Punkt steht dann irgendwo und sieht aus wie ein Wert. */
        mesh.visible = load > 0.02
        const scale = 0.4 + 0.6 * load
        mesh.scale.setScalar(scale)
      }
    }

    if (flags.grf) {
      const weight = dynamics.mass * 9.81
      for (const [side, ref] of [
        ['R', grfR],
        ['L', grfL],
      ] as const) {
        const group = ref.current
        if (!group) continue
        const force = dynamics.grf[side][i]
        const magnitude = Math.hypot(force.x, force.y, force.z)
        const share = weight > 0 ? magnitude / weight : 0
        group.visible = share > 0.02
        if (!group.visible) continue

        const foot = dynamics.cop[side][i]
        group.position.set(foot.x, 0, foot.z)
        /* Der Pfeil zeigt in die Richtung der Kraft und ist so lang wie ihr
           Betrag. Beides zugleich — ein Pfeil fester Länge mit richtiger
           Richtung verschwiege die Hälfte. */
        const length = share / GRF_PER_METRE
        group.scale.set(1, Math.max(0.001, length), 1)
        const dir = new THREE.Vector3(force.x, force.y, force.z).normalize()
        group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir)
      }
    }
  })

  return (
    <group>
      {flags.markers && (
        <instancedMesh ref={markers} args={[undefined, undefined, markerCount]}>
          <sphereGeometry args={[0.022, 12, 10]} />
          <meshBasicMaterial color={accent} toneMapped={false} />
        </instancedMesh>
      )}

      {flags.paths &&
        paths.map((path) => (
          <line key={path.key}>
            <bufferGeometry>
              <bufferAttribute attach="attributes-position" args={[path.points, 3]} />
            </bufferGeometry>
            <lineBasicMaterial color={accent} transparent opacity={0.5} toneMapped={false} />
          </line>
        ))}

      {flags.com && (
        <>
          <mesh ref={comBall}>
            <sphereGeometry args={[0.045, 20, 16]} />
            <meshBasicMaterial color={warm} toneMapped={false} />
          </mesh>
          <line>
            <bufferGeometry>
              <bufferAttribute attach="attributes-position" args={[comPath, 3]} />
            </bufferGeometry>
            <lineBasicMaterial color={warm} transparent opacity={0.8} toneMapped={false} />
          </line>
        </>
      )}

      {flags.cop &&
        ([copR, copL] as const).map((ref, k) => (
          <mesh key={k} ref={ref} rotation={[-Math.PI / 2, 0, 0]}>
            <ringGeometry args={[0.018, 0.03, 20]} />
            <meshBasicMaterial color={warm} toneMapped={false} side={THREE.DoubleSide} />
          </mesh>
        ))}

      {flags.grf &&
        ([grfR, grfL] as const).map((ref, k) => (
          /* Der Pfeil ist in seiner Gruppe ein Meter lang und wird über die
             Gruppe skaliert — so bleibt die Spitze am Ende und wächst nicht mit. */
          <group key={k} ref={ref}>
            <mesh position={[0, 0.5, 0]}>
              <cylinderGeometry args={[0.008, 0.008, 1, 8]} />
              <meshBasicMaterial color={warm} toneMapped={false} />
            </mesh>
            <mesh position={[0, 1, 0]}>
              <coneGeometry args={[0.026, 0.07, 10]} />
              <meshBasicMaterial color={warm} toneMapped={false} />
            </mesh>
          </group>
        ))}
    </group>
  )
}

/**
 * Die Marke an einer angeklickten Achse.
 *
 * DAS PROBLEM, DAS SIE LÖST. Eine Antriebstabelle nennt eine Achse „Knie
 * sagittal rechts" und daneben 306 Nm. Wer die Maschine nicht im Kopf hat,
 * weiss damit, WIE VIEL, aber nicht WO — und lernt aus der Zahl nichts über
 * die Konstruktion. Ein Ring an genau diesem Gelenk beantwortet die Frage
 * ohne eine einzige Zeile Text.
 *
 * SIE LIEST DIESELBE BAHN WIE DIE GELENKMARKER. `dynamics.tracks` führt die
 * gefilterte Bahn jedes benannten Punktes über den Zyklus; die Marke greift
 * mit demselben Index darauf zu wie alles andere in dieser Datei. Die Pose des
 * Rigs abzufragen wäre der zweite Weg zu derselben Zahl — und der, der
 * irgendwann ein Bild hinterherhinkt.
 *
 * ZWEI RINGE UND EIN SENKRECHTER STRICH, kein Text. Beschriftung im Raum
 * bräuchte entweder ein Schriftbild als Textur oder eine HTML-Ebene über dem
 * Bild, die bei jedem Drehen nachgerechnet wird. Der Name steht deshalb als
 * Marke ÜBER dem Fenster — dort ist er immer lesbar, auch wenn das Gelenk
 * gerade hinter dem Körper liegt.
 *
 * DER RING DREHT SICH ZUR KAMERA. Ein fest gelegter Ring verschwindet in der
 * Seitenansicht zu einem Strich, und genau die Seitenansicht ist die, in der
 * man ein Beingelenk anschaut.
 */
/**
 * Der nächstgelegene GEFÜHRTE Punkt für Gelenke, deren Bahn nicht abgetastet wird.
 *
 * Die Bahnen werden für die Kinetik geführt, nicht für die Beschriftung — die
 * Rumpfkette braucht keine gefilterte Punktbahn, weil an ihr kein Moment gegen
 * den Boden gerechnet wird. Eine Achse, die in der Freigangsliste steht, hat
 * deshalb nicht zwingend eine eigene Bahn.
 *
 * Statt sie unmarkiert zu lassen, zeigt die Marke auf den Punkt, an dem das
 * Gelenk SITZT — das Lendengelenk am Becken, das Brustgelenk am Rumpf. Das ist
 * ein paar Zentimeter ungenau und beantwortet die Frage trotzdem; gar keine
 * Marke beantwortet sie nicht.
 */
const NEAR_TRACK: Record<string, string> = {
  lumbar: 'pelvis',
  thorax: 'trunk',
  neck: 'head',
  pelvis: 'pelvis',
}

export function FocusMarker({
  simulation,
  focus,
  phase,
  warm,
}: {
  simulation: Simulation
  focus: { joint: string; side: Side | null }
  phase: MutableRefObject<number>
  warm: THREE.ColorRepresentation
}) {
  const { dynamics } = simulation
  const n = dynamics.samples
  const group = useRef<THREE.Group>(null)
  const pulse = useRef<THREE.Group>(null)

  /* Die Bahn wird EINMAL herausgesucht und nicht in jedem Bild: sie hängt am
     Durchlauf und an der gewählten Achse, und beide stehen still. */
  const track = useMemo(() => {
    const t = dynamics.tracks
    const near = NEAR_TRACK[focus.joint] ?? focus.joint
    return (
      t[`${focus.joint}${focus.side ?? ''}`] ??
      t[focus.joint] ??
      t[`${near}${focus.side ?? ''}`] ??
      t[near] ??
      null
    )
  }, [dynamics, focus.joint, focus.side])

  useFrame(({ camera, clock }) => {
    const node = group.current
    if (!node) return
    if (!track) {
      node.visible = false
      return
    }
    node.visible = true
    const i = Math.min(n - 1, Math.max(0, Math.floor(phase.current * n)))
    const p = track[i]
    node.position.set(p.x, p.y, p.z)
    node.quaternion.copy(camera.quaternion)

    /* Ein langsames Atmen. Ohne es verschwindet der Ring zwischen
       den Kanten der Aktuatoren — er ist dann ein Kreis unter vielen. */
    if (pulse.current) {
      const s = 1 + 0.16 * Math.sin(clock.elapsedTime * 2.6)
      pulse.current.scale.setScalar(s)
    }
  })

  if (!track) return null

  return (
    <group ref={group}>
      <group ref={pulse}>
        <mesh>
          <ringGeometry args={[0.062, 0.076, 40]} />
          <meshBasicMaterial color={warm} toneMapped={false} side={THREE.DoubleSide} />
        </mesh>
      </group>
      <mesh>
        <ringGeometry args={[0.03, 0.036, 32]} />
        <meshBasicMaterial color={warm} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      {/* Ein Strich nach oben, damit die Marke auch dann auffällt, wenn das
          Gelenk gerade von einem Arm verdeckt wird. */}
      <mesh position={[0, 0.16, 0]}>
        <boxGeometry args={[0.004, 0.16, 0.004]} />
        <meshBasicMaterial color={warm} toneMapped={false} />
      </mesh>
    </group>
  )
}
